import * as React from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Paper from '@mui/material/Paper';
import { visuallyHidden } from '@mui/utils';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@mui/material';
import { Form, FormListType } from './FormList';

interface Data {
  formNumber: string;
  formTitle: string;
  dateModified: string;
  userEmail: string;
}

const STATUS_ALL: string = 'SAVED';

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

type Order = 'asc' | 'desc';

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (a: { [key in Key]: number | string }, b: { [key in Key]: number | string }) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// Since 2020 all major browsers ensure sort stability with Array.prototype.sort().
// stableSort() brings sort stability to non-modern browsers (notably IE11). If you
// only support modern browsers you can replace stableSort(exampleArray, exampleComparator)
// with exampleArray.slice().sort(exampleComparator)
function stableSort<T>(array: readonly T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

interface HeadCell {
  id: keyof Data;
  label: string;
  width: number;
}

const headCells: readonly HeadCell[] = [
  {
    id: 'formNumber',
    label: 'Form Number',
    width: 0.25,
  },
  {
    id: 'formTitle',
    label: 'Form Title',
    width: 0.4,
  },
  {
    id: 'userEmail',
    label: 'User Email',
    width: 0.2,
  },
  {
    id: 'dateModified',
    label: 'Date Modified',
    width: 0.15,
  },
];

interface SortableTableHeadProps {
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof Data) => void;
  order: Order;
  orderBy: string;
}

function SortableTableHead(props: SortableTableHeadProps) {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler = (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align="left"
            sx={{ width: headCell.width }}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

function SkeletonTable() {
  return (
    <TableRow>
      <TableCell component="th" scope="row">
        <Skeleton variant="text" />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" />
      </TableCell>
    </TableRow>
  );
}

interface SortableTableProps {
  rows: Form[];
  email: string;
  filter: string;
  isLoading: boolean;
  formListType: FormListType;
}

export default function SortableTable({
  rows,
  email,
  filter,
  isLoading,
  formListType,
}: SortableTableProps) {
  const [order, setOrder] = React.useState<Order>('desc');
  const [orderBy, setOrderBy] = React.useState<keyof Data>('dateModified');
  const navigate = useNavigate();

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof Data) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const visibleRows = React.useMemo(
    () => stableSort(rows, getComparator(order, orderBy)),
    [order, orderBy, rows]
  );

  return (
    <Box sx={{ width: 1 }}>
      <Paper sx={{ width: 1, mb: 2 }}>
        <TableContainer>
          <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size="medium">
            <SortableTableHead order={order} orderBy={orderBy} onRequestSort={handleRequestSort} />
            <TableBody>
              {isLoading && Array.from(Array(5).keys()).map((arr) => <SkeletonTable key={arr} />)}
              {!isLoading &&
                visibleRows.map((row, index) => {
                  const labelId = `formListTable-${index}`;

                  return (
                    <TableRow
                      hover
                      tabIndex={-1}
                      key={row.id + row.dateModified}
                      className={
                        filter === 'all' &&
                        formListType === 'UNPUBLISHED' &&
                        row.user.email === email &&
                        row.status !== STATUS_ALL
                          ? 'bg-color__light-green'
                          : ''
                      }
                      sx={{
                        '&:last-child td, &:last-child th': { border: 0 },
                        cursor: 'pointer',
                      }}
                      onClick={() => navigate(`/forms/${row.id}`)}
                    >
                      <TableCell component="th" id={labelId} scope="row">
                        {row.formNumber}
                      </TableCell>
                      <TableCell>{row.formTitle}</TableCell>
                      <TableCell>{row.userEmail}</TableCell>
                      <TableCell>{moment(row.dateModified).format('MM/DD/YY h:mm A')}</TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
